import { type MouseEvent, type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Info from '@/assets/icons/info.svg?component';
import { type IconComponent, SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip, { type TooltipPlacement } from '@/components/atoms/Tooltip';

import styles from './InfoTip.module.scss';

/**
 * The button's name: a `topic` such as the card title makes "About {topic}"; a `label` is a
 * finished name. Either way a short name, never the explanation itself.
 */
type InfoTipName = { topic: string; label?: never } | { label: string; topic?: never };

export type InfoTipProps = InfoTipName & {
    /** The explanation the tooltip shows; a screen reader reads it as the button's description. */
    content: ReactNode;
    heading?: ReactNode;
    /** `inherit` sizes the icon to the text around it, for a tip right after a value. */
    size?: 'small' | 'inherit';
    icon?: IconComponent;
    placement?: TooltipPlacement;
    maxWidth?: number;
    /** Where the tip sits, e.g. absolutely in a corner. */
    className?: string;
};

// Inside a form label a click must not move focus into the field. It still bubbles: the
// tooltip's own click handler is what ends a tap, so the bubble can close afterwards.
function keepFieldUnfocused(event: MouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
}

/** A small info icon that explains what is beside it, on hover, tap or keyboard focus. */
export default function InfoTip(props: InfoTipProps) {
    const {
        content,
        heading,
        size = 'small',
        icon = Info,
        placement = 'top',
        maxWidth,
        className,
    } = props;
    const { t } = useTranslation();
    const descriptionId = useId();

    return (
        <Tooltip
            content={content}
            heading={heading}
            placement={placement}
            maxWidth={maxWidth}
            layout="inline"
            targetClassName={className}>
            <button
                type="button"
                className={classNames(styles.infoTip, size === 'inherit' && styles.inherit)}
                aria-label={
                    props.label === undefined
                        ? t('atoms.infoTip.about', { topic: props.topic })
                        : props.label
                }
                aria-describedby={descriptionId}
                onClick={keepFieldUnfocused}>
                <SvgIcon
                    icon={icon}
                    size={size === 'inherit' ? 'inherit' : 'small'}
                    color="currentColor"
                    svgProps={{ 'aria-hidden': true }}
                />
            </button>
            {/* An element named by aria-describedby describes the button while hidden, so the
                explanation reaches a screen reader, which does not read the tooltip. */}
            <span id={descriptionId} hidden>
                {heading}
                {heading != null && ' '}
                {content}
            </span>
        </Tooltip>
    );
}

export { InfoTip };
